import * as DropdownMenu from '@wandb/weave/components/DropdownMenu';
import {isEqual} from 'lodash';
import React, {useCallback, useMemo} from 'react';
import {toast} from 'react-toastify';

import {trackAddPanels} from '../../../../services/analytics/addPanelsEvents';
import {trackPanelBankSection} from '../../../../services/analytics/panelBankSectionEvents';
import {useDispatch} from '../../../../state/hooks';
import {usePanelBankUpdaterContext} from '../../../../state/panelbank/context';
import * as ViewHooks from '../../../../state/views/hooks';
import * as PanelBankConfigActions from '../../../../state/views/panelBankConfig/actions';
import * as PanelBankSectionConfigActions from '../../../../state/views/panelBankSectionConfig/actions';
import * as PanelBankSectionConfigTypes from '../../../../state/views/panelBankSectionConfig/types';
import {isMobile} from '../../../../util/panelbankFlow';
import {
  OverflowMenu,
  OverflowMenuItem,
} from '../../../Views/components/OverflowMenu';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useWorkspaceDrawerContext} from '../../../WorkspaceDrawer/WorkspaceDrawerContextProvider';

const MAX_CUSTOM_PANEL_COUNT = 50; // panel limit for custom layout (grid) sections

export const PanelBankSectionOverflowMenu = ({
  isProjectReadOnly,
  panelBankSectionConfigRef,
  sectionComponentRef,
  setIsEditingName,
}: {
  isProjectReadOnly?: boolean;
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref;
  sectionComponentRef: React.RefObject<HTMLDivElement>;
  setIsEditingName: (isEditing: boolean) => void;
}) => {
  const {
    addPanelsClick,
    addSectionAbove,
    addSectionBelow,
    addSectionToReport,
    deleteSection,
    sortPanels,
  } = usePanelBankSectionOverflowMenuActions(panelBankSectionConfigRef);

  if (isMobile()) {
    return null;
  }

  return (
    <OverflowMenu
      aria-label="Section actions"
      data-test="pb-section-overflow-menu-trigger"
      isRootModal={false} // modal=true interferes with input autofocus on "Rename section"
    >
      <OverflowMenuItem
        data-test="add-panels"
        displayName="Add panels"
        icon="add-new"
        onSelect={() => {
          addPanelsClick();
          sectionComponentRef.current?.scrollIntoView({
            block: 'start',
            inline: 'start',
            behavior: 'smooth',
          });
        }}
      />
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="pencil-edit"
        displayName="Rename section"
        onSelect={() => setIsEditingName(true)}
      />
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="sort"
        displayName="Sort panels A-Z"
        onSelect={sortPanels}
      />
      <DropdownMenu.Separator />
      <OverflowMenuItem
        icon="new-section-above"
        displayName="New section above"
        onSelect={addSectionAbove}
      />
      <OverflowMenuItem
        icon="new-section-below"
        displayName="New section below"
        onSelect={addSectionBelow}
      />
      {!isProjectReadOnly && (
        <>
          <DropdownMenu.Separator />
          <OverflowMenuItem
            icon="add-to-report"
            displayName="Add section to report"
            onSelect={addSectionToReport}
          />
        </>
      )}
      <DropdownMenu.Separator />
      <OverflowMenuItem
        data-test="delete-section"
        displayName="Delete section"
        icon="delete"
        onSelect={deleteSection}
      />
    </OverflowMenu>
  );
};

const usePanelBankSectionOverflowMenuActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const {addSectionAbove, addSectionBelow, addSectionToReport, deleteSection} =
    useSectionActions(panelBankSectionConfigRef);
  const {addPanelsClick, sortPanels} = useSectionPanelsActions(
    panelBankSectionConfigRef
  );

  return useMemo(
    () => ({
      addPanelsClick,
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
      sortPanels,
    }),
    [
      addPanelsClick,
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
      sortPanels,
    ]
  );
};

const useToggleSectionlayout = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref,
  sectionLayoutType: 'grid' | 'flow',
  activePanelCount: number
) => {
  const toggleType = ViewHooks.useViewAction(
    panelBankSectionConfigRef,
    PanelBankSectionConfigActions.toggleType
  );
  const toggleSectionLayout = useCallback(() => {
    // Flip the value because this is a toggle
    trackPanelBankSection({
      action: sectionLayoutType === 'grid' ? 'standard grid' : 'custom grid',
      numPanels: activePanelCount,
    });

    if (
      sectionLayoutType !== 'grid' &&
      activePanelCount > MAX_CUSTOM_PANEL_COUNT
    ) {
      toast(`Custom Layout is limited to ${MAX_CUSTOM_PANEL_COUNT} panels.`);
    } else {
      toggleType();
    }
  }, [sectionLayoutType, activePanelCount, toggleType]);

  return toggleSectionLayout;
};

const useSectionActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  const {setExportingSectionRefID} = usePanelBankUpdaterContext();
  const {targetSectionRef: workspaceDrawerTarget, closeWorkspaceDrawer} =
    useWorkspaceDrawerContext();

  const addSectionToReport = useCallback(() => {
    setExportingSectionRefID(panelBankSectionConfigRef.id);
  }, [setExportingSectionRefID, panelBankSectionConfigRef]);

  // adding section above or below
  const addSectionAction = ViewHooks.useViewAction(
    panelBankConfigRef,
    PanelBankConfigActions.addSection
  );
  const addSectionAbove = useCallback(() => {
    addSectionAction(panelBankSectionConfigRef);
  }, [addSectionAction, panelBankSectionConfigRef]);
  const addSectionBelow = useCallback(() => {
    addSectionAction(panelBankSectionConfigRef, {addAfter: true});
  }, [addSectionAction, panelBankSectionConfigRef]);

  // delete section
  const deleteSectionAction = ViewHooks.useViewActionBindAll(
    PanelBankConfigActions.deleteSection,
    panelBankConfigRef,
    panelBankSectionConfigRef,
    workspaceSettingsRef
  );
  const deleteSection = useCallback(() => {
    if (isEqual(panelBankSectionConfigRef, workspaceDrawerTarget)) {
      closeWorkspaceDrawer();
    }
    deleteSectionAction();
  }, [
    closeWorkspaceDrawer,
    deleteSectionAction,
    panelBankSectionConfigRef,
    workspaceDrawerTarget,
  ]);

  return useMemo(
    () => ({
      addSectionAbove,
      addSectionBelow,
      addSectionToReport,
      deleteSection,
    }),
    [addSectionAbove, addSectionBelow, addSectionToReport, deleteSection]
  );
};

const useSectionPanelsActions = (
  panelBankSectionConfigRef: PanelBankSectionConfigTypes.Ref
) => {
  const dispatch = useDispatch();
  const {openWorkspaceDrawer} = useWorkspaceDrawerContext();

  const addPanelsClick = useCallback(() => {
    openWorkspaceDrawer('add-panels', panelBankSectionConfigRef);
    trackAddPanels({
      action: 'Drawer opened',
      scope: 'section',
    });
  }, [openWorkspaceDrawer, panelBankSectionConfigRef]);

  const sortPanels = useCallback(() => {
    return dispatch(
      PanelBankSectionConfigActions.sortPanels([panelBankSectionConfigRef])
    );
  }, [dispatch, panelBankSectionConfigRef]);

  return useMemo(
    () => ({
      addPanelsClick,
      sortPanels,
    }),
    [addPanelsClick, sortPanels]
  );
};
